import { create } from 'zustand';
import { useAuthStore, outgoingAccountId } from './authStore';
import { localOnlySummary } from '../services/accountData';
import { localHistoryWorkspaceIds, localHistoryScopeFolders, forgetLocalHistoryLinks } from '../services/git/localHistories';
import { isTauri } from '../utils/tauri';
import { forgetPlanClaim } from '../plans/planClaim';

/**
 * The explicit Sign out, with a question when it would lose something
 * (24.09.2026).
 *
 * An explicit sign-out purges the account's local data (PR #204, for shared
 * machines). Since boards and documents can live "On this device" only
 * (cloud/local items C2/C3), that purge silently erased data with no other
 * copy, and so did any change still waiting to sync. Now:
 *
 *   - nothing local-only -> sign out and purge, as before, no dialog;
 *   - something local-only, or we cannot tell -> the dialog lists it and asks:
 *       keep  -> purge everything the server has, keep the rest in this
 *                account's own folder (another account signing in cannot
 *                reach it);
 *       remove -> the full purge, as before;
 *       cancel -> stay signed in.
 *
 * Every Sign out button goes through `requestSignOut`; the dialog
 * (`SignOutLocalDataDialog`) is mounted once, in ModalStack.
 *
 * Workspace histories tracked with git on this device only (04.10.2026) count
 * as local-only too: with any, the dialog always opens and names them; Keep
 * hands their ids to the purge (`keepMirrorIds`), which keeps their mirror
 * folders; Remove deletes them with the rest. The same for "My projects,
 * boards & documents" tracked on this device (T10): `localScopeFolders` →
 * `keepScopeMirrors`.
 */

const REASON = 'user_sign_out';

// Refused changes (`refusedChanges`) are deliberately not part of this: nothing
// ever clears them, so counting them would ask on every sign-out forever
// (review of #245). The dialog still mentions them when it opens anyway.
const hasAnything = (s) =>
  (s?.deviceBoards || 0) + (s?.deviceDocuments || 0) + (s?.deviceOther || 0) + (s?.unsyncedChanges || 0) > 0;

/** Never throws: a failed lookup must not block the sign-out — it keeps nothing extra. */
async function readLocalHistoryIds() {
  try {
    return await localHistoryWorkspaceIds();
  } catch (err) {
    console.warn('[signOut] could not list local-only workspace histories:', err);
    return [];
  }
}

/** Never throws, like readLocalHistoryIds: the personal scope's local history folder (T10), if any. */
async function readLocalScopeFolders() {
  try {
    return await localHistoryScopeFolders();
  } catch (err) {
    console.warn('[signOut] could not read the local-only scope history:', err);
    return [];
  }
}

/** `keepLocalOnly` logout options, plus the histories to keep when there are any. */
const keepOptions = (ids, folders = []) => ({
  keepLocalOnly: true,
  ...(ids.length > 0 ? { keepMirrorIds: ids } : {}),
  ...(folders.length > 0 ? { keepScopeMirrors: folders } : {}),
});

export const useSignOutStore = create((set, get) => ({
  /**
   * null, or `{ summary, localHistoryIds, localScopeFolders }` while the dialog is open.
   * `summary` null = unknown; `localHistoryIds` = workspaces whose git history
   * is on this device only.
   */
  prompt: null,
  busy: false,

  requestSignOut: async () => {
    if (get().busy || get().prompt) return;
    const { logout, user } = useAuthStore.getState();
    // The web build has no local account folder: nothing to lose, nothing to ask.
    if (!isTauri()) {
      await logout({ purge: true, reason: REASON });
      return;
    }
    const accountId = outgoingAccountId(user);
    // No account to name: the purge refuses to run without an id anyway
    // (purgeAccountData), so there is nothing a dialog could protect.
    if (accountId == null) {
      await logout({ purge: true, reason: REASON });
      return;
    }
    set({ busy: true });
    let summary = null;
    let localHistoryIds = [];
    let localScopeFolders = [];
    try {
      [summary, localHistoryIds, localScopeFolders] = await Promise.all([localOnlySummary(accountId), readLocalHistoryIds(), readLocalScopeFolders()]);
    } finally {
      set({ busy: false });
    }
    // Nothing local-only, or an encrypted database the purge keeps whole
    // either way: sign out without asking. Still in keep mode — the logout
    // awaits the network before it purges, and a change queued in that window
    // must not be the one thing this path deletes (review of #245). With
    // nothing local-only, keep mode removes exactly what the full purge does.
    // A local-only git history always asks: the encrypted database being kept
    // whole says nothing about the mirror folders, which the purge removes.
    if (localHistoryIds.length === 0 && localScopeFolders.length === 0 && summary && (summary.databaseKeptWhole || !hasAnything(summary))) {
      await logout({ purge: true, reason: REASON, keepLocalOnly: true });
      return;
    }
    set({ prompt: { summary, localHistoryIds, localScopeFolders } });
  },

  /** `choice`: 'keep' | 'remove'. */
  confirm: async (choice) => {
    const prompt = get().prompt;
    if (!prompt || get().busy) return;
    set({ busy: true });
    const keep = choice === 'keep';
    const ids = keep && Array.isArray(prompt.localHistoryIds) ? prompt.localHistoryIds : [];
    const folders = keep && Array.isArray(prompt.localScopeFolders) ? prompt.localScopeFolders : [];
    try {
      // Remove deletes these histories, so their links go with them (owner, 05.10.2026).
      const doomed = [...(prompt.localHistoryIds || []), ...(prompt.localScopeFolders || [])];
      if (!keep && doomed.length > 0) await forgetLocalHistoryLinks(prompt.localHistoryIds, prompt.localScopeFolders);
      // Plans v2 (owner, 16.10.2026): "Remove from device" also forgets the
      // cached signed plan claim; a plain sign-out ("Keep") keeps it.
      // Not awaited: best effort, and the sign-out itself must not wait on it.
      if (!keep) forgetPlanClaim();
      await useAuthStore.getState().logout({
        purge: true,
        reason: keep ? 'user_sign_out_keep_local' : REASON,
        ...(keep ? keepOptions(ids, folders) : { keepLocalOnly: false, removeLocalHistories: true }),
      });
    } finally {
      set({ prompt: null, busy: false });
    }
  },

  cancel: () => {
    if (get().busy) return;
    set({ prompt: null });
  },
}));

export const requestSignOut = () => useSignOutStore.getState().requestSignOut();
